/**
 * OtpStep — shared OTP verification step for both Email and Phone channels.
 *
 * Behavior per spec (Task 6.2):
 *   - Auto-POST the send endpoint once on mount (via a ref guard so StrictMode
 *     double-invoke does not double-send; guarded also by a per-session flag).
 *   - Renders the custom `OtpInput` (from `features/auth/components/OtpInput`)
 *     — 6 single-digit inputs with auto-advance and paste support.
 *   - "Send again" button shown after `retry_after_seconds` countdown reaches 0.
 *   - 6+ wrong attempts → auto-resend (after 60s countdown) + lock error display.
 *   - `aria-live="polite"` on the error region.
 *
 * The component is channel-agnostic; the concrete `EmailOtpStep` and
 * `PhoneOtpStep` wrappers inject the i18n key prefix and API functions.
 */
import { useEffect, useRef, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Info, Loader2 } from "lucide-react";

import { OtpInput } from "@/features/auth/components/OtpInput";
import { SELLER_REG_NS } from "@/features/seller-registration/api";
import { ApiError } from "@/lib/api-client";

const RESEND_SECONDS = 60;
const MAX_WRONG_BEFORE_AUTOLOCK = 6;

const STEP_H1_CLASS =
  "text-[27px] font-bold leading-tight tracking-[-0.03em] text-foreground";

export type OtpChannel = "email" | "phone";

export interface OtpStepProps {
  channel: OtpChannel;
  /** Email address or phone number shown in the "sent to" lede. */
  sentTo: string;
  applicationId: string;
  /** auto-POST send on mount. Injected for testability. */
  apiSend: (applicationId: string) => Promise<{ ok: boolean; retry_after_seconds: number }>;
  /** Verify the OTP. Injected for testability. */
  apiVerify: (
    applicationId: string,
    otp: string,
  ) => Promise<{ ok: boolean; next: string }>;
  onVerified: () => void;
}

export function OtpStep({
  channel,
  sentTo,
  applicationId,
  apiSend,
  apiVerify,
  onVerified,
}: OtpStepProps): ReactElement {
  const { t } = useTranslation(SELLER_REG_NS);
  const ns = channel === "email" ? "emailOtp" : "phoneOtp";

  const [otpValue, setOtpValue] = useState("");
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [retryAfter, setRetryAfter] = useState(0);
  const [wrongCount, setWrongCount] = useState(0);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const hasSentRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Auto-send on mount (once per session mount, not per StrictMode double-invoke).
  useEffect(() => {
    if (hasSentRef.current) return;
    hasSentRef.current = true;
    void doSend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Countdown timer for resend.
  useEffect(() => {
    if (retryAfter <= 0) return;
    timerRef.current = setInterval(() => {
      setRetryAfter((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [retryAfter]);

  async function doSend(): Promise<void> {
    setIsSending(true);
    setErrorKey(null);
    try {
      const res = await apiSend(applicationId);
      setRetryAfter(res.retry_after_seconds);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        const secs = err.retryAfterSeconds ?? RESEND_SECONDS;
        setRetryAfter(secs);
        setErrorKey(`${ns}.errRate`);
      } else {
        setErrorKey(`${ns}.errSend`);
      }
    } finally {
      setIsSending(false);
    }
  }

  async function doVerify(code: string): Promise<void> {
    if (isVerifying) return;
    setIsVerifying(true);
    setErrorKey(null);
    try {
      await apiVerify(applicationId, code);
      onVerified();
    } catch (err) {
      setOtpValue("");
      const newCount = wrongCount + 1;
      setWrongCount(newCount);
      if (err instanceof ApiError) {
        if (err.status === 429 || err.code === "seller_application.otp.lockout") {
          setErrorKey(`${ns}.errLock`);
          if (newCount >= MAX_WRONG_BEFORE_AUTOLOCK) {
            // Auto-resend after 60s
            setRetryAfter(RESEND_SECONDS);
            void doSend();
          }
        } else {
          setErrorKey(`${ns}.errWrong`);
        }
      } else {
        setErrorKey(`${ns}.errGeneric`);
      }
    } finally {
      setIsVerifying(false);
    }
  }

  const handleComplete = (code: string): void => {
    void doVerify(code);
  };

  const handleResend = (): void => {
    hasSentRef.current = true;
    void doSend();
  };

  const errorId = `otp-${channel}-error`;

  return (
    <div className="flex flex-1 flex-col justify-center gap-8">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <h1 className={STEP_H1_CLASS} data-testid={`${channel}-otp-h1`}>
          {t(`${ns}.h1`)}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t(`${ns}.lede`)}{" "}
          <span className="font-medium text-foreground">{sentTo}</span>
        </p>
      </div>

      {/* QA environment notice — SMS delivery is not yet enabled, so the
          phone code will not arrive. Phone channel only. */}
      {channel === "phone" ? (
        <div
          role="note"
          data-testid="phone-otp-demo-notice"
          className="flex items-start gap-2.5 rounded-[var(--g-radius)] border border-[var(--g-divider)] bg-[var(--g-surface)] p-3 text-left"
        >
          <Info
            size={16}
            className="mt-0.5 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
          <p className="text-sm text-muted-foreground">
            {t("phoneOtp.demoNotice")}
          </p>
        </div>
      ) : null}

      {/* OTP input */}
      <div className="flex flex-col gap-4">
        <OtpInput
          value={otpValue}
          onChange={setOtpValue}
          onComplete={handleComplete}
          label={t(`${ns}.codeLabel`)}
          disabled={isVerifying || isSending}
          invalid={errorKey !== null}
          errorId={errorId}
          testId={`${channel}-otp-cell`}
        />

        {/* Error region */}
        <div
          id={errorId}
          role="status"
          aria-live="polite"
          className="min-h-[20px] text-center text-sm"
          data-testid={`${channel}-otp-error`}
        >
          {errorKey !== null ? (
            <span className="text-destructive">
              {t(errorKey, { seconds: retryAfter })}
            </span>
          ) : null}
        </div>

        {/* Resend control */}
        <div className="flex justify-center">
          {retryAfter > 0 || isSending ? (
            <span className="text-sm text-muted-foreground" data-testid={`${channel}-otp-countdown`}>
              {isSending ? (
                <Loader2 size={14} className="inline animate-spin" aria-hidden="true" />
              ) : (
                `${t("cta.sendAgainIn")} ${retryAfter} с`
              )}
            </span>
          ) : (
            <button
              type="button"
              onClick={handleResend}
              data-testid={`${channel}-otp-resend`}
              className={[
                "min-h-[44px] px-3 text-sm font-medium text-primary",
                "underline-offset-4 hover:underline",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                "focus-visible:ring-offset-2",
              ].join(" ")}
            >
              {t("cta.sendAgain")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
